/**
 * <product-spec-tabs> — tabbed product details with thumbnail-driven media
 * switching inside each panel.
 *
 * Tab switching uses ARIA tabs / tabpanels with arrow-key navigation.
 * Thumbs carry data-legacy-target="<slide-key>" and switch the visible
 * slide (.spec-tabs__legacy-slide[data-legacy-slide=...]) inside the
 * panel. When the user navigates away from a panel or slide, any playing
 * video inside the leaving area is paused.
 */
class ProductSpecTabs extends HTMLElement {
  #controller = new AbortController();

  connectedCallback() {
    const { signal } = this.#controller;
    this.#bindTabs(signal);
    this.#bindThumbs(signal);
    this.#bindHashRouting(signal);
  }

  disconnectedCallback() {
    this.#controller.abort();
  }

  // ---- tab nav ----------------------------------------------------------

  #bindTabs(signal) {
    const tabs = this.querySelectorAll('[role="tab"][data-tab-index]');
    tabs.forEach((tab) => {
      tab.addEventListener(
        'click',
        () => this.#activateTab(Number(tab.dataset.tabIndex)),
        { signal }
      );
      tab.addEventListener(
        'keydown',
        (event) => {
          const allTabs = [...this.querySelectorAll('[role="tab"][data-tab-index]')];
          const currentIdx = allTabs.indexOf(tab);
          let nextIdx = null;
          if (event.key === 'ArrowRight') nextIdx = (currentIdx + 1) % allTabs.length;
          if (event.key === 'ArrowLeft') nextIdx = (currentIdx - 1 + allTabs.length) % allTabs.length;
          if (event.key === 'Home') nextIdx = 0;
          if (event.key === 'End') nextIdx = allTabs.length - 1;
          if (nextIdx !== null) {
            event.preventDefault();
            this.#activateTab(nextIdx);
            allTabs[nextIdx].focus();
          }
        },
        { signal }
      );
    });
  }

  #activateTab(idx) {
    this.querySelectorAll('[role="tab"][data-tab-index]').forEach((tab) => {
      const isActive = Number(tab.dataset.tabIndex) === idx;
      tab.setAttribute('aria-selected', isActive ? 'true' : 'false');
      tab.tabIndex = isActive ? 0 : -1;
    });
    this.querySelectorAll('[role="tabpanel"]').forEach((panel) => {
      const isActive = Number(panel.dataset.panelIndex) === idx;
      if (isActive) {
        panel.removeAttribute('hidden');
        // Autoplay only fires for elements that were visible at parse time;
        // panels that start hidden need a manual nudge once they're shown.
        this.#playVideosIn(panel);
      } else {
        panel.setAttribute('hidden', '');
        this.#pauseVideosIn(panel);
      }
    });
  }

  // ---- hash routing (#pas-features / #pas-sizing) ----------------------

  #bindHashRouting(signal) {
    const route = () => {
      const hash = window.location.hash.replace('#', '');
      if (!hash) return;
      // Map hash → tab index by metafield_source written on tab buttons.
      const target = this.querySelector('[data-tab-anchor="' + hash + '"]');
      if (!target) return;
      this.#activateTab(Number(target.dataset.tabIndex));
      // Scroll the section into view after a tick so the tab activation
      // doesn't fight the browser's default anchor scroll.
      requestAnimationFrame(() => {
        this.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
    };
    window.addEventListener('hashchange', route, { signal });
    route();
  }

  // ---- thumb nav (feature + legacy modes) ------------------------------

  #bindThumbs(signal) {
    this.querySelectorAll('.spec-tabs__thumb').forEach((thumb) => {
      thumb.addEventListener(
        'click',
        () => this.#handleThumbClick(thumb),
        { signal }
      );
    });
  }

  #handleThumbClick(thumb) {
    const panel = thumb.closest('[role="tabpanel"]');
    if (!panel) return;

    if (thumb.dataset.legacyTarget !== undefined) {
      this.#switchSlide(panel, thumb.dataset.legacyTarget);
    }

    panel
      .querySelectorAll('.spec-tabs__thumb')
      .forEach((t) => t.classList.remove('is-active'));
    thumb.classList.add('is-active');
  }

  #switchSlide(panel, key) {
    panel.querySelectorAll('.spec-tabs__legacy-slide').forEach((slide) => {
      const isActive = slide.dataset.legacySlide === key;
      if (isActive) {
        slide.removeAttribute('hidden');
        slide.classList.add('is-active');
        // Restart any video inside the newly-visible slide. Autoplay only
        // fires once on initial load, so we have to nudge it manually when
        // the user toggles back to it.
        this.#playVideosIn(slide);
      } else {
        slide.setAttribute('hidden', '');
        slide.classList.remove('is-active');
        this.#pauseVideosIn(slide);
      }
    });
  }

  #playVideosIn(scope) {
    scope.querySelectorAll('video').forEach((v) => {
      // Don't nudge videos that live inside a hidden slide/feature — only
      // the currently visible one should be playing.
      if (v !== scope && v.closest('[hidden]')) return;
      try {
        v.currentTime = 0;
        const p = v.play();
        if (p && typeof p.catch === 'function') p.catch(() => {});
      } catch (_) {
        /* ignore */
      }
    });
  }

  // ---- helpers ---------------------------------------------------------

  #pauseVideosIn(scope) {
    scope.querySelectorAll('video').forEach((v) => {
      try {
        v.pause();
      } catch (_) {
        /* ignore */
      }
    });
    // For YouTube/Vimeo iframes there is no synchronous "pause"; the simplest
    // reliable approach is to reload the iframe so playback stops cleanly.
    scope.querySelectorAll('iframe').forEach((iframe) => {
      const src = iframe.getAttribute('src');
      if (src) iframe.setAttribute('src', src);
    });
  }
}

if (!customElements.get('product-spec-tabs')) {
  customElements.define('product-spec-tabs', ProductSpecTabs);
}
